// 図形の背景色（fill）の選好を localStorage に保存する。
// 付箋・ペンと同じく hex 文字列（"#RRGGBB"）で色を扱い、組み込みプリセットと利用者が
// 追加したカスタム色（最大 SHAPE_FILL_CUSTOM_COLOR_LIMIT 件）から選ぶ。
// 既定は「背景なし」（SHAPE_FILL_NONE）で、従来どおり塗りのない図形を配置する。

import { isValidHexColor, normalizeColorForCompare, normalizeHexColor } from "./pen-preference";
import { addColorToList, removeColorFromList, updateColorInList } from "./custom-color-list";
import type { ShapeCanvasObject, StrokePattern } from "@refinement-canvas/domain";
import { resolveStrokePattern } from "./stroke-pattern";

const FILL_KEY = "refinement-canvas:shape:fill";
const CUSTOM_COLORS_KEY = "refinement-canvas:shape:custom-colors";
const STROKE_KEY = "refinement-canvas:shape:stroke";
const STROKE_CUSTOM_COLORS_KEY = "refinement-canvas:shape:stroke-custom-colors";
const STROKE_WIDTH_KEY = "refinement-canvas:shape:stroke-width";
const STROKE_PATTERN_KEY = "refinement-canvas:shape-stroke-pattern";

// 図形の背景色は hex 文字列、または「背景なし」を表すセンチネル。
export type ShapeFill = string;

/** 「背景なし」を表すセンチネル値。図形作成時は fill を未設定にする。 */
export const SHAPE_FILL_NONE = "none";

/**
 * 図形の背景を「なし」にするときに op へ送る fill 値。
 * op スキーマは「未設定」を表現できないため、透明色を明示的に設定して塗りなし表示にする
 * （描画は `fill="transparent"` 相当）。
 *
 * 既定値として localStorage へ保存する表現は {@link SHAPE_FILL_NONE}（"none"）で、
 * op へ送る表現とは異なる点に注意する。相互変換は「op へ送るとき」「既定値へ保存するとき」の
 * 2 か所だけで行い、それ以外では "transparent" 表現に統一する。
 */
export const SHAPE_FILL_TRANSPARENT = "transparent";

/** 図形の既定の枠線色。既存の stroke 未設定オブジェクトもこの色で描画する。 */
export const SHAPE_DEFAULT_STROKE = "#1a1a2e";
/** localStorage 上で「線なし」を表すセンチネル値。 */
export const SHAPE_STROKE_NONE = "none";
/** op とオブジェクト上で「線なし」を表す値。 */
export const SHAPE_STROKE_TRANSPARENT = "transparent";

/** 図形の枠線幅プリセット。 */
export type ShapeStrokeWidth = "thin" | "medium" | "thick";

/** 図形の枠線幅プリセットと実際の px 値の対応。 */
export const SHAPE_STROKE_WIDTH_PIXELS: Readonly<Record<ShapeStrokeWidth, number>> = {
  thin: 1,
  medium: 2,
  thick: 4,
};

/** strokeWidth 未設定の既存図形に補う既定線幅。 */
export const SHAPE_DEFAULT_STROKE_WIDTH = SHAPE_STROKE_WIDTH_PIXELS.medium;

/** 図形の枠線色に使用する濃色プリセット。 */
export const SHAPE_STROKE_PRESET_COLORS: readonly string[] = [
  SHAPE_DEFAULT_STROKE,
  "#d22b2b",
  "#1e63d4",
  "#0f8a4d",
  "#e08b00",
  "#6b6f7d",
];

/** ローカル保持できる図形枠線色のカスタム色の上限。 */
export const SHAPE_STROKE_CUSTOM_COLOR_LIMIT = 12;

/**
 * 図形の背景色を比較用の表現へ揃える。
 *
 * 未設定・`"transparent"`・`"none"` はいずれも「背景なし」として同一視する。
 * `"none"` を含めるのは、`shape-object.tsx` が `fill={object.fill ?? "transparent"}` を SVG の
 * `fill` 属性へ渡しており、SVG では `fill="none"` も塗りなしとして描画されるため
 * （`update_canvas_object` は任意文字列の fill を保存でき、既定値の保存表現も `"none"` なので
 * 実データに現れ得る）。ここで寄せておかないと、見た目は背景なしなのにパネルでは未選択になる。
 *
 * その他の非 hex 値（`red` / `rgba(...)` など）は「背景なし」へ寄せない。
 */
export function normalizeShapeFillForCompare(fill: string | undefined): string {
  if (fill === undefined || fill === SHAPE_FILL_TRANSPARENT || fill === SHAPE_FILL_NONE) {
    return SHAPE_FILL_TRANSPARENT;
  }
  return normalizeColorForCompare(fill);
}

/**
 * 図形の枠線色を比較用の表現へ揃える。
 * 未設定は実際の描画と同じ既定色へ、「none」と「transparent」は透明へ寄せる。
 */
export function normalizeShapeStrokeForCompare(stroke: string | undefined): string {
  if (stroke === undefined) return normalizeColorForCompare(SHAPE_DEFAULT_STROKE);
  if (stroke === SHAPE_STROKE_TRANSPARENT || stroke === SHAPE_STROKE_NONE) {
    return SHAPE_STROKE_TRANSPARENT;
  }
  return normalizeColorForCompare(stroke);
}

/** 矢印へ適用する線色を求め、線なしの場合は図形が消えないよう既定色へ戻す。 */
export function resolveArrowStrokeColor(stroke: string): string {
  return normalizeShapeStrokeForCompare(stroke) === SHAPE_STROKE_TRANSPARENT
    ? SHAPE_DEFAULT_STROKE
    : stroke;
}

/** px 値を枠線幅プリセットへ逆引きする。プリセット外の値は混在表示と同じ null。 */
export function resolveShapeStrokeWidthKind(px: number | undefined): ShapeStrokeWidth | null {
  const effective = px ?? SHAPE_DEFAULT_STROKE_WIDTH;
  for (const kind of Object.keys(SHAPE_STROKE_WIDTH_PIXELS) as ShapeStrokeWidth[]) {
    if (SHAPE_STROKE_WIDTH_PIXELS[kind] === effective) return kind;
  }
  return null;
}

/**
 * 新規図形を作るとき、選好を永続オブジェクトの style 値へ変換する。
 * 矢印は fill を持たず、線なしが選ばれていても既定色で可視性を保つ。
 */
export function resolveShapeCreateStyle(input: {
  readonly shapeKind: ShapeCanvasObject["shapeKind"];
  readonly fill: ShapeFill;
  readonly stroke: string;
  readonly strokeWidth: ShapeStrokeWidth;
  readonly strokePattern?: StrokePattern | undefined;
}): {
  readonly fill?: string | undefined;
  readonly stroke: string;
  readonly strokePattern: StrokePattern;
  readonly strokeWidth: number;
} {
  const resolvedStroke =
    input.shapeKind === "arrow" ? resolveArrowStrokeColor(input.stroke) : input.stroke;
  return {
    ...(input.shapeKind !== "arrow" && input.fill !== SHAPE_FILL_NONE ? { fill: input.fill } : {}),
    stroke:
      normalizeShapeStrokeForCompare(resolvedStroke) === SHAPE_STROKE_TRANSPARENT
        ? SHAPE_STROKE_TRANSPARENT
        : resolvedStroke,
    strokePattern: resolveStrokePattern(input.strokePattern),
    strokeWidth: SHAPE_STROKE_WIDTH_PIXELS[input.strokeWidth],
  };
}

// 図形の輪郭が見えるよう、薄め（淡彩）のプリセットを用意する。付箋プリセットと近い系統。
export const SHAPE_FILL_PRESET_COLORS: readonly string[] = [
  "#fff5b3",
  "#ffd6e7",
  "#dbeafe",
  "#dcfce7",
  "#ede9fe",
  "#f3f4f6",
];

const DEFAULT_FILL: ShapeFill = SHAPE_FILL_NONE;

/** ローカル保持できる図形背景色のカスタム色の上限。 */
export const SHAPE_FILL_CUSTOM_COLOR_LIMIT = 12;

/**
 * 図形の背景色の選好を読み込む。
 * 保存値が "none"（背景なし）または有効な hex のときだけ採用し、それ以外は既定（背景なし）。
 */
export function loadShapeFill(): ShapeFill {
  try {
    const value = window.localStorage.getItem(FILL_KEY);
    if (value === SHAPE_FILL_NONE) return SHAPE_FILL_NONE;
    if (isValidHexColor(value)) return normalizeHexColor(value);
    return DEFAULT_FILL;
  } catch {
    return DEFAULT_FILL;
  }
}

export function saveShapeFill(fill: ShapeFill): void {
  try {
    if (fill === SHAPE_FILL_NONE) {
      window.localStorage.setItem(FILL_KEY, SHAPE_FILL_NONE);
      return;
    }
    if (!isValidHexColor(fill)) return;
    window.localStorage.setItem(FILL_KEY, normalizeHexColor(fill));
  } catch {
    // localStorage が使えない環境では保存のみ諦める。
  }
}

/** 追加保存された図形背景色のカスタム色一覧を読み込む。壊れた値は読み飛ばす。 */
export function loadShapeCustomColors(): readonly string[] {
  try {
    const raw = window.localStorage.getItem(CUSTOM_COLORS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const result: string[] = [];
    for (const value of parsed) {
      if (isValidHexColor(value)) {
        const normalized = normalizeHexColor(value);
        if (!result.includes(normalized)) result.push(normalized);
      }
      if (result.length >= SHAPE_FILL_CUSTOM_COLOR_LIMIT) break;
    }
    return result;
  } catch {
    return [];
  }
}

/**
 * 図形背景色のカスタム色を 1 件追加する。
 * - 組み込みプリセットに含まれる色は重複としてカスタム側へは追加しない。
 * - 既にカスタム色に存在する場合は追加せず、現在のリストを返す。
 * - 上限 {@link SHAPE_FILL_CUSTOM_COLOR_LIMIT} に達している場合は古い方を 1 件捨てる（FIFO）。
 *
 * 返り値は更新後のカスタム色一覧。
 */
export function addShapeCustomColor(color: string): readonly string[] {
  const current = loadShapeCustomColors();
  const next = addColorToList(
    current,
    color,
    SHAPE_FILL_PRESET_COLORS,
    SHAPE_FILL_CUSTOM_COLOR_LIMIT
  );
  if (next === current) return current;
  try {
    window.localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
  return next;
}

export function updateShapeCustomColor(
  previous: string,
  next: string
): { readonly colors: readonly string[]; readonly applied: boolean } {
  const current = loadShapeCustomColors();
  const result = updateColorInList(current, previous, next, SHAPE_FILL_PRESET_COLORS);
  if (result.applied) {
    try {
      window.localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify(result.colors));
    } catch {
      // ignore
    }
  }
  return result;
}

export function removeShapeCustomColor(color: string): readonly string[] {
  const current = loadShapeCustomColors();
  const next = removeColorFromList(current, color);
  if (next !== current) {
    try {
      window.localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  }
  return next;
}

/** 図形の枠線色の選好を読み込む。壊れた保存値は既定色へ戻す。 */
export function loadShapeStroke(): string {
  try {
    const value = window.localStorage.getItem(STROKE_KEY);
    if (value === SHAPE_STROKE_NONE) return SHAPE_STROKE_NONE;
    if (isValidHexColor(value)) return normalizeHexColor(value);
    return SHAPE_DEFAULT_STROKE;
  } catch {
    return SHAPE_DEFAULT_STROKE;
  }
}

export function saveShapeStroke(stroke: string): void {
  try {
    if (stroke === SHAPE_STROKE_NONE) {
      window.localStorage.setItem(STROKE_KEY, stroke);
      return;
    }
    if (!isValidHexColor(stroke)) return;
    window.localStorage.setItem(STROKE_KEY, normalizeHexColor(stroke));
  } catch {
    // localStorage が使えない環境では保存のみ諦める。
  }
}

/** 図形の枠線幅の選好を読み込む。 */
export function loadShapeStrokeWidth(): ShapeStrokeWidth {
  try {
    const value = window.localStorage.getItem(STROKE_WIDTH_KEY);
    return value === "thin" || value === "medium" || value === "thick" ? value : "medium";
  } catch {
    return "medium";
  }
}

export function saveShapeStrokeWidth(width: ShapeStrokeWidth): void {
  try {
    window.localStorage.setItem(STROKE_WIDTH_KEY, width);
  } catch {
    // localStorage が使えない環境では保存のみ諦める。
  }
}

/** 図形の線種選好。欠落・未知値・読取失敗はいずれも実線へ戻す。 */
export function loadShapeStrokePattern(): StrokePattern {
  try {
    return resolveStrokePattern(window.localStorage.getItem(STROKE_PATTERN_KEY));
  } catch {
    return "solid";
  }
}

export function saveShapeStrokePattern(pattern: StrokePattern): void {
  try {
    window.localStorage.setItem(STROKE_PATTERN_KEY, pattern);
  } catch {
    // localStorage が使えない環境では現在セッションの選択だけを維持する。
  }
}

/** 追加保存された図形枠線色のカスタム色一覧を読み込む。 */
export function loadShapeStrokeCustomColors(): readonly string[] {
  try {
    const raw = window.localStorage.getItem(STROKE_CUSTOM_COLORS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const result: string[] = [];
    for (const value of parsed) {
      if (isValidHexColor(value)) {
        const normalized = normalizeHexColor(value);
        if (!result.includes(normalized)) result.push(normalized);
      }
      if (result.length >= SHAPE_STROKE_CUSTOM_COLOR_LIMIT) break;
    }
    return result;
  } catch {
    return [];
  }
}

export function addShapeStrokeCustomColor(color: string): readonly string[] {
  const current = loadShapeStrokeCustomColors();
  const next = addColorToList(
    current,
    color,
    SHAPE_STROKE_PRESET_COLORS,
    SHAPE_STROKE_CUSTOM_COLOR_LIMIT
  );
  if (next === current) return current;
  try {
    window.localStorage.setItem(STROKE_CUSTOM_COLORS_KEY, JSON.stringify(next));
  } catch {
    // localStorage が使えない環境では保存のみ諦める。
  }
  return next;
}

export function updateShapeStrokeCustomColor(
  previous: string,
  next: string
): { readonly colors: readonly string[]; readonly applied: boolean } {
  const current = loadShapeStrokeCustomColors();
  const result = updateColorInList(current, previous, next, SHAPE_STROKE_PRESET_COLORS);
  if (result.applied) {
    try {
      window.localStorage.setItem(STROKE_CUSTOM_COLORS_KEY, JSON.stringify(result.colors));
    } catch {
      // localStorage が使えない環境では保存のみ諦める。
    }
  }
  return result;
}

export function removeShapeStrokeCustomColor(color: string): readonly string[] {
  const current = loadShapeStrokeCustomColors();
  const next = removeColorFromList(current, color);
  if (next !== current) {
    try {
      window.localStorage.setItem(STROKE_CUSTOM_COLORS_KEY, JSON.stringify(next));
    } catch {
      // localStorage が使えない環境では保存のみ諦める。
    }
  }
  return next;
}
